import {
  fieldToFilter,
  type OwnerType,
  type SaleInput,
  type SaleWithId,
  type Tasa,
} from '@/shared/schema';
import { FirestorePaths } from '@/shared/lib/firestore-paths';
import { MoneyUtils } from '@/shared/utils/money-utils';
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  documentId,
  increment,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { BalanceService } from './balance-service';
import { AnalyticsService } from '@/shared/analytics/analytics-service';

const saleCollectionRef = (ownerId: string) =>
  collection(db, FirestorePaths.sales(ownerId));

const saleDocRef = (ownerId: string, saleId: string) =>
  doc(db, FirestorePaths.saleDoc(ownerId, saleId));

const findSaleRefById = async (saleId: string, ownerId?: string) => {
  if (ownerId) {
    return saleDocRef(ownerId, saleId);
  }

  const snapshot = await getDocs(
    query(
      collectionGroup(db, 'sales'),
      where(documentId(), '==', saleId),
      limit(1),
    ),
  );

  if (snapshot.empty) return null;
  return snapshot.docs[0].ref;
};

export const SaleService = {
  // ==========================================
  // DOMINIO 1: PROFESIONAL (El Barbero/Estilista)
  // ==========================================

  getByClient: async (
    uid: string,
    ownerType: OwnerType,
    clientId: string,
  ): Promise<SaleWithId[]> => {
    const q = query(
      saleCollectionRef(uid),
      where(fieldToFilter(ownerType), '==', uid),
      where('client.uid', '==', clientId),
      orderBy('audit.createdAt', 'desc'),
    );

    const snapshot = await getDocs(q);
    const sales = snapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    })) as SaleWithId[];
    console.log(sales);
    return sales;
  },

  update: async (
    saleId: string,
    data: Partial<SaleInput> & Record<string, any>,
    ownerId?: string,
  ): Promise<void> => {
    const ref = await findSaleRefById(saleId, ownerId);
    if (!ref) throw new Error('Venta no encontrada');

    await updateDoc(ref, {
      ...data,
      'audit.updatedAt': serverTimestamp(),
    });
  },
  // ==========================================
  // ACCIONES COMPARTIDAS
  // ==========================================

  getById: async (saleId: string, ownerId?: string): Promise<SaleWithId | null> => {
    const ref = await findSaleRefById(saleId, ownerId);
    if (!ref) return null;
    const snapshot = await getDoc(ref);
    if (!snapshot.exists()) return null;

    const data = snapshot.data();
    if (data.audit.deletedAt) return null;
    return data;
  },
  /**
   * 🛒 CREAR VENTA (Senior Normalization)
   */
  create: async (params: {
    data: SaleInput;
    creatorId: string;
    appointmentId?: string;
    rates: Tasa;
  }) => {
    const { data, creatorId, appointmentId, rates } = params;
    const businessId = data.businessId;
    const ownerId = businessId || data.professionalId;
    const isIndependent = !businessId;
    if (!ownerId) {
      throw new Error('Owner ID is required to create a sale');
    }

    console.group('🔍 [DEEP DEBUG] SALE CREATION TRACE');

    // --- Step 1: Resolve Contract/Payment Model ---
    let effectiveCommissionPct = data.commission_percentage ?? 0;
    let paymentModel: 'commission' | 'fixed_rent' = 'commission';

    if (businessId) {
      const businessSnap = await getDoc(doc(db, 'owners', businessId));
      if (businessSnap.exists()) {
        const businessData = businessSnap.data();
        const workerConfig = businessData.workers?.find(
          (w: any) => w.uid === data.professionalId,
        );

        paymentModel = workerConfig?.paymentModel || 'commission';

        if (paymentModel === 'fixed_rent') {
          // 🛡️ Rule: Renters keep 100% of the gross (Price - Materials)
          effectiveCommissionPct = 100;
        } else {
          effectiveCommissionPct =
            workerConfig?.commissionPercentage ?? effectiveCommissionPct;
        }
      }
    } else {
      // If truly independent account, they are their own boss (100% comm)
      effectiveCommissionPct = 100;
    }

    console.log(
      `📊 [DEBUG] Model: ${paymentModel} | Comm: ${effectiveCommissionPct}%`,
    );

    // --- Step 2: Item Calculation (Math Only) ---
    let totalGrossAcc = 0;
    let totalCommAcc = 0;
    let totalMaterialsAcc = 0;

    const itemsWithSnapshots = data.items.map((item) => {
      const priceAcc = MoneyUtils.convert(
        item.price,
        item.currency,
        data.accounting_currency,
        rates,
      );
      const matAcc = MoneyUtils.convert(
        item.material_cost_applied || 0,
        item.material_cost_currency || 'USD',
        data.accounting_currency,
        rates,
      );

      // Commission is calculated on the taxable base:
      // $$ \text{Commission} = (\text{Price} - \text{Materials}) \times \text{Percentage} $$
      const taxableBase = Math.max(0, priceAcc - matAcc);
      const commAcc = (taxableBase * effectiveCommissionPct) / 100;

      totalGrossAcc += priceAcc;
      totalMaterialsAcc += matAcc;
      totalCommAcc += commAcc;

      return {
        ...item,
        price_accounting_ref: priceAcc,
        commission_amount_accounting: commAcc,
      };
    });

    // --- Step 3: Advance Absorption ---
    let totalPaidAcc = 0;
    const processedPayments = (data.payments || []).map((p) => {
      // Respect the frozen accounting amount from the snapshot if available.
      // This prevents phantom debts if exchange rates change between booking and sale.
      let amtAcc = p.amount_accounting;

      if (amtAcc === undefined || amtAcc === null) {
        console.warn(
          `[DEBUG] Payment snapshot missing amount_accounting, recalculating at current rate.`,
        );
        amtAcc = MoneyUtils.convert(
          p.amount_local,
          p.method.currency,
          data.accounting_currency,
          rates,
        );
      }

      totalPaidAcc += amtAcc;
      return { ...p, amount_accounting: MoneyUtils.round(amtAcc) };
    });

    const pendingAcc = Math.max(
      0,
      MoneyUtils.round(totalGrossAcc - totalPaidAcc),
    );
    const isAlreadyPaid = pendingAcc <= 0.01;
    const totalBusinessIncome = MoneyUtils.round(
      totalGrossAcc - totalCommAcc - totalMaterialsAcc,
    );

    // --- Step 4: Construct Final Sale Object ---
    const finalSale: any = {
      ...data,
      items: itemsWithSnapshots,
      payments: processedPayments,
      total_accounting: MoneyUtils.round(totalGrossAcc),
      amount_paid_accounting: MoneyUtils.round(totalPaidAcc),
      amount_pending_accounting: isAlreadyPaid ? 0 : pendingAcc,
      status: isAlreadyPaid ? 'paid' : 'pending',
      revenue_released: isAlreadyPaid, // 👈 Accrual: Revenue is recognized ONLY if already paid

      // Metadata for the applyPaymentDB trigger
      paymentModel,
      commission_percentage: effectiveCommissionPct,
      commission_accounting: MoneyUtils.round(totalCommAcc),
      total_material_cost_accounting: MoneyUtils.round(totalMaterialsAcc),
      business_income_accounting: totalBusinessIncome,
      is_commissionable: paymentModel === 'commission',

      audit: {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: creatorId,
      },
    };

    const saleRef = doc(saleCollectionRef(ownerId));
    const checkoutCode = saleRef.id.slice(0, 6).toUpperCase();
    const saleDoc = saleDocRef(ownerId, saleRef.id);

    finalSale.checkout = {
      ...(finalSale.checkout || {}),
      code: checkoutCode,
      status: isAlreadyPaid ? 'paid' : 'pending',
    };

    // --- Step 5: Transaction ---

    try {
      await runTransaction(db, async (transaction) => {
        // --- 1. ALL READS FIRST ---
        let clientSnap = null;
        let clientRef = null;
        if (isAlreadyPaid && data.client?.uid) {
          clientRef = doc(db, 'owners', ownerId, 'clients', data.client.uid);
          clientSnap = await transaction.get(clientRef);
        }

        // --- 2. ALL WRITES SECOND ---
        // 1. Save Sale Document
        transaction.set(saleDoc, finalSale);

        // 2. Revenue Release Trigger (Closure Audit if fully paid by advance)
        if (isAlreadyPaid) {
          console.log(
            '🎊 [DEBUG] Sale created already paid. Executing Closure Audit.',
          );

          // Sum Cash-in-Hand for the professional
          let workerCashInHand = 0;
          for (const p of processedPayments) {
            if (p.receiverType === 'professional') {
              workerCashInHand += p.amount_accounting;
            }
          }

          // Net Adjustment
          const netAdjustment = totalCommAcc - workerCashInHand;

          if (data.professionalId) {
            const workerBalanceRef = doc(
              db,
              'owners',
              ownerId,
              'balances',
              data.professionalId,
            );
            transaction.set(
              workerBalanceRef,
              {
                last_updated: serverTimestamp(),
                balance_accounting: increment(netAdjustment),
                period_earned_accounting: increment(totalCommAcc),
                period_materials_accounting: increment(totalMaterialsAcc), // 👈 Atribuimos insumos al profesional
                lifetime_earned_accounting: increment(totalCommAcc),
                period_services_count: increment(1),
                total_services_done: increment(1),
              },
              { merge: true },
            );
          }

          if (paymentModel === 'commission' && !isIndependent) {
            const revenueUpdates = BalanceService.prepareRevenueRelease({
              total_gross: totalGrossAcc,
              commission: totalCommAcc,
              materials: totalMaterialsAcc,
              isIndependent: false,
            });
            const mainBalanceRef = doc(
              db,
              'owners',
              ownerId,
              'balances',
              'main',
            );
            transaction.set(mainBalanceRef, revenueUpdates, { merge: true });
          }

          // 3. Update Client Stats (If paid)
          if (clientSnap && clientRef) {
            const clientData = clientSnap.data() as any;
            const clientUpdates: any = {
              'audit.updatedAt': serverTimestamp(),
              total_spent_accounting: increment(totalGrossAcc),
              total_visits: increment(1),
              last_visit_date: serverTimestamp(),
            };

            // Set first visit date only if it doesn't exist
            if (!clientData?.first_visit_date) {
              clientUpdates.first_visit_date = serverTimestamp();
            }

            transaction.update(clientRef, clientUpdates);
          }

          // --- 4. ACADEMY ENROLLMENTS (If any course items exist) ---
          const activationLinks: Array<{ courseId: string; token: string }> = [];
          for (const item of itemsWithSnapshots) {
            if (item.courseId) {
              const enrollmentRef = doc(collection(db, FirestorePaths.enrollments(ownerId)));
              const activationToken = Math.random().toString(36).substring(2, 15) + 
                                Math.random().toString(36).substring(2, 15);
                                
              const enrollmentData = {
                businessId: ownerId,
                clientId: data.client.uid,
                courseId: item.courseId,
                saleId: saleDoc.id,
                source: 'sale',
                status: 'pending',
                activationToken,
                completedLessons: [],
                audit: {
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                  createdBy: creatorId,
                }
              };
              
              transaction.set(enrollmentRef, enrollmentData);
              activationLinks.push({ courseId: item.courseId, token: activationToken });
              console.log(`🎓 [DEBUG] Enrollment invitation created for course: ${item.courseId}`);
            }
          }

          // Store activation links on the sale document for admin reference
          if (activationLinks.length > 0) {
            transaction.update(saleDoc, {
              'academy.activationLinks': activationLinks,
            });
          }
        } else {
          console.log(
            '⏳ [DEBUG] Sale created pending. No balances updated yet.',
          );
        }

        if (appointmentId) {
          transaction.update(doc(db, 'appointments', appointmentId), {
            status: 'completed',
            saleId: saleDoc.id,
          });
        }
      });

      return { ...finalSale, id: saleDoc.id };
    } catch (e: any) {
      console.error('❌ Sale Creation Failed:', e);
      AnalyticsService.trackError(e, 'SaleService.create');
      throw e;
    } finally {
      console.groupEnd();
    }
  },

  /**
   * 🔍 QUERIES ACTUALIZADAS
   */
  getSalesByProfessional: async (
    businessId: string | null,
    professionalId: string,
    limitCount?: number,
  ): Promise<SaleWithId[]> => {
    const constraints: any[] = [where('professionalId', '==', professionalId)];
    if (businessId) constraints.push(where('businessId', '==', businessId));

    constraints.push(orderBy('audit.createdAt', 'desc'));
    if (limitCount) constraints.push(limit(limitCount));

    const q = query(saleCollectionRef(businessId || professionalId), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (doc) => ({ ...doc.data(), id: doc.id }) as SaleWithId,
    );
  },
  Business: {
    getGlobalSales: async (businessId: string): Promise<SaleWithId[]> => {
      const q = query(
        saleCollectionRef(businessId),
        where('businessId', '==', businessId),
        orderBy('audit.createdAt', 'desc'),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(
        (doc) => ({ ...doc.data(), id: doc.id }) as SaleWithId,
      );
    },
  },

  getPending: async (
    ownerId: string,
    ownerType: OwnerType,
  ): Promise<SaleWithId[]> => {
    // Query both 'pending' and 'verifying' sales, ordered by creation date
    const [pendingSnap, verifyingSnap] = await Promise.all([
      getDocs(
        query(
          saleCollectionRef(ownerId),
          where(fieldToFilter(ownerType), '==', ownerId),
          where('status', '==', 'pending'),
          orderBy('audit.createdAt', 'desc'),
        ),
      ),
      getDocs(
        query(
          saleCollectionRef(ownerId),
          where(fieldToFilter(ownerType), '==', ownerId),
          where('status', '==', 'verifying'),
          orderBy('audit.createdAt', 'desc'),
        ),
      ),
    ]);

    const pending = pendingSnap.docs.map(
      (doc) => ({ ...doc.data(), id: doc.id }) as SaleWithId,
    );
    const verifying = verifyingSnap.docs.map(
      (doc) => ({ ...doc.data(), id: doc.id }) as SaleWithId,
    );

    // Merge and sort by createdAt desc (newest first)
    return [...pending, ...verifying].sort((a, b) => {
      const aDate = a.audit?.createdAt?.toDate?.() || new Date(0);
      const bDate = b.audit?.createdAt?.toDate?.() || new Date(0);
      return bDate.getTime() - aDate.getTime();
    });
  },

  getPeriodSales: async (params: {
    businessId: string;
    workerId?: string; // If 'main', it's for the whole business
    startDate: any;
    endDate: any;
  }): Promise<SaleWithId[]> => {
    const { businessId, workerId, startDate, endDate } = params;
    
    const constraints: any[] = [
      where('businessId', '==', businessId),
      where('status', '==', 'paid'), // We only care about paid sales for analytics
    ];

    if (workerId && workerId !== 'main' && workerId !== businessId) {
      constraints.push(where('professionalId', '==', workerId));
    }

    if (startDate) {
      constraints.push(where('audit.createdAt', '>=', startDate));
    }
    
    if (endDate) {
      constraints.push(where('audit.createdAt', '<=', endDate));
    }

    constraints.push(orderBy('audit.createdAt', 'desc'));

    const q = query(saleCollectionRef(businessId), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (doc) => ({ ...doc.data(), id: doc.id }) as SaleWithId,
    );
  },

  delete: async (saleId: string, userId: string): Promise<void> => {
    const ref = await findSaleRefById(saleId);
    if (!ref) throw new Error('Venta no encontrada');
    await updateDoc(ref, {
      'audit.deletedAt': serverTimestamp(),
      'audit.updatedBy': userId,
    });
  },
};
